'use client';

import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const trustPoints = [
  {
    icon: 'ShieldCheckIcon' as const,
    title: 'HIPAA-Compliant',
    description: 'Patient information is handled with full HIPAA compliance on every trip, every time.',
  },
  {
    icon: 'DocumentCheckIcon' as const,
    title: 'Fully Licensed & Insured',
    description: 'All vehicles and drivers carry NJ NEMT certification, full commercial insurance, and background checks.',
  },
  {
    icon: 'TruckIcon' as const,
    title: '12-Vehicle Fleet',
    description: '12-vehicle fleet — stretcher-equipped ambulances and wheelchair-accessible Mobility Assistance Vehicles (MAVs). Sanitized between every transport.',
  },
  {
    icon: 'UserGroupIcon' as const,
    title: 'Trained Caregivers',
    description: 'Our drivers are trained in patient handling, lift operation, and first aid — not just driving.',
  },
  {
    icon: 'ClockIcon' as const,
    title: 'On-Time, Every Time',
    description: 'We track appointments, plan routes, and call ahead. Missing a dialysis session is never an option.',
  },
  {
    icon: 'CreditCardIcon' as const,
    title: 'All Major Insurance + Private Pay',
    description: 'We accept most major insurance plans, Medicaid transport, and private pay with transparent pricing.',
  },
];

export default function WhyChooseSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.reveal-hidden');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 px-6 md:px-10 bg-secondary"
      aria-labelledby="why-choose-heading"
    >
      <div className="max-w-screen-xl mx-auto">

        {/* Header — 4/8 asymmetric like T2 amenities */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-4">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
              Why Kapra
            </span>
            <h2
              id="why-choose-heading"
              className="text-section-xl font-display font-light text-primary mb-6"
            >
              Built for{' '}
              <span className="italic text-muted-foreground">trust.</span>
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-8 max-w-xs">
              When you&rsquo;re arranging transport for someone you love, you need more than a driver. You need a team that cares.
            </p>
            <a
              href="tel:+15513269573"
              className="hover-arrow-link text-primary font-bold"
              aria-label="Call (551) 326-9573 to learn more"
            >
              Call to discuss your needs
              <Icon name="ArrowRightIcon" size={14} />
            </a>
          </div>

          {/* 6-point grid — 3×2 */}
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-2xl overflow-hidden">
            {trustPoints.map((point, idx) => (
              <div
                key={point.title}
                className="reveal-hidden bg-card p-8 flex flex-col justify-between hover:bg-muted transition-colors group"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <Icon
                  name={point.icon}
                  size={28}
                  className="text-primary mb-auto group-hover:text-accent transition-colors"
                  variant="outline"
                />
                <div className="mt-6">
                  <h3 className="font-display text-lg font-medium text-foreground mb-2">
                    {point.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {point.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}